import React, { useEffect, useState, useContext, useRef } from "react";
import getServices from "../../Services/get-service";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import { format, parseISO } from "date-fns";
import Constant from "../../Constant";
import Note from "../../Assets/images/note.svg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { toast } from "react-toastify";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#CCF2E1",
    color: "black",
    fontWeight: "600",
    fontSize: 14,
    borderBottom: "none",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#1A1A1B",
    // padding: "16px 24px",
    borderBottom: "1px solid #D2D3D5",
  },
}));

function Transactions() {
  const [transaction, setTransaction] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dropdown states
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDuration, setSelectedDuration] = useState("all");

  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isDurationOpen, setIsDurationOpen] = useState(false);

  const [searchVal, setSearchVal] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Refs for closing dropdowns when clicking outside
  const typeRef = useRef(null);
  const statusRef = useRef(null);
  const durationRef = useRef(null);

  const typeOptions = [
    { label: "All transactions", value: "all" },
    { label: "Credit", value: "credit" },
    { label: "Debit", value: "debit" },
  ];

  const statusOptions = [
    { label: "All Status", value: "all" },
    { label: "Success", value: "success" },
    { label: "Failed", value: "failed" },
    { label: "Pending", value: "pending" },
    { label: "Reversed", value: "reversed" },
  ];

  const durationOptions = [
    { label: "All Time", value: "all" },
    { label: "Last 7 days", value: "7days" },
    { label: "Last 1 month", value: "1month" },
    { label: "Last 3 month", value: "3month" },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeRef.current && !typeRef.current.contains(event.target)) {
        setIsTypeOpen(false);
      }
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setIsStatusOpen(false);
      }
      if (durationRef.current && !durationRef.current.contains(event.target)) {
        setIsDurationOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchTransactions = async () => {
    let walletId = localStorage.getItem("walletNumber");
    setLoading(true);
    try {
      const response = await getServices.getRecentTransaction(walletId);
      if (response.data.data) {
        setTransaction(response.data.data);
      } else {
        setTransaction([]);
        toast.error(response.data.description);
      }
    } catch (error) {
      // console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    window.addEventListener("walletRefresh", fetchTransactions);
    return () => window.removeEventListener("walletRefresh", fetchTransactions);
  }, []);

  const getTransactionType = (transaction) => {
    const type = (transaction.transType || "").toUpperCase();
    if (type === "C") return "Credit";
    if (type === "D") return "Debit";
    return type;
  };
  const getTransactionTypeColor = (tran) => {
    const type = (tran.transType || "").toUpperCase();
    if (type === "C") return "#00BC67";
    if (type === "D") return "#FF5A5F";
  };

  const formatDate = (dateStr) => {

    if (!dateStr) return "";
    try {
      const formattedDate = dateStr.replace(" ", "T");
      return format(parseISO(formattedDate), "hh:mm a - d MMMM, yyyy");
    } catch (error) {
      console.log(error);
    }
  };

  const formatAmount = (tran) => {
    const amt = tran.amount || 0;
    return `NGN ${Constant.FormatNumber(amt)}`;
  };

  const getStatusLabel = (tran) => {
    const status = (tran.transactionStatus || "").toLowerCase().trim();
    if (status === "success") {
      return "Success";
    }
    if (status === "failed") {
      return "Failed";
    }
    if (status === "pending") {
      return "Pending";
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColor = (statusLabel) => {
    switch (statusLabel) {
      case "Success":
        return "#00BC67";
      case "Failed":
        return "#FF5A5F";
      case "Pending":
        return "#D98634";
      default:
        return "#808285";
    }
  };

  // Filter transaction
  const displayData = (transaction || []).filter((tran) => {
    // 1. Transaction Type filter
    if (selectedType !== "all") {
      let type = (tran.transType || "").toLowerCase();
      if (type === "c") type = "credit";
      if (type === "d") type = "debit";
      if (!type.includes(selectedType)) {
        return false;
      }
    }

    // 2. Status filter
    if (selectedStatus !== "all") {
      const status = (tran.transactionStatus || "").toLowerCase().trim();
      if (!status.includes(selectedStatus)) {
        return false;
      }
    }

    // 3. Duration filter
    if (selectedDuration !== "all" && tran.transactionDate) {
      try {
        const isoStr = tran.transactionDate.includes(" ")
          ? tran.transactionDate.replace(" ", "T")
          : tran.transactionDate;
        const txDate = parseISO(isoStr);
        const now = new Date();
        const diffMs = now - txDate;
        const diffDays = diffMs / (1000 * 60 * 60 * 24);

        if (selectedDuration === "2days" && diffDays > 2) {
          return false;
        }
        if (selectedDuration === "7days" && diffDays > 7) {
          return false;
        }
        if (selectedDuration === "1week" && diffDays > 7) {
          return false;
        }
      } catch (e) {
        console.error(e);
      }
    }

    // 4. Search query filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase().trim();
      const txId = (tran.transactionId || "").toLowerCase();
      const ref = (tran.reference || "").toLowerCase();
      const payRef = (tran.paymentReference || "").toLowerCase();
      if (!txId.includes(q) && !ref.includes(q) && !payRef.includes(q)) {
        return false;
      }
    }

    return true;
  });

  return (
    <div className="mt-8">
      {/* Header and Filter controls */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6">
        <p className="text-xl md:text-2xl text-back-color2 font-[600] cursor-pointer">
          Transactions
        </p>

        <div className="flex flex-wrap items-center gap-3">
          {/* All transactions dropdown */}
          <div ref={typeRef} className="relative">
            <div
              onClick={() => setIsTypeOpen(!isTypeOpen)}
              className="flex items-center gap-2 bg-[#F8F9FA] hover:bg-[#F0F1F2] transition-colors px-4 py-2.5 rounded-lg cursor-pointer text-[#1A1A1B] text-[14px] font-[500] border border-[#E6E6E7]"
            >
              <span>
                {typeOptions.find((opt) => opt.value === selectedType)?.label ||
                  "All transactions"}
              </span>
              <KeyboardArrowDownIcon
                style={{ fontSize: "18px", color: "#1A1A1B" }}
              />
            </div>
            {isTypeOpen && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-white border border-[#E6E6E7] rounded-lg shadow-lg z-50 py-1">
                {typeOptions.map((opt) => (
                  <div
                    key={opt.value}
                    className={`px-4 py-2 hover:bg-[#F8F9FA] cursor-pointer text-[14px] ${selectedType === opt.value
                      ? "text-[#00BC67] font-[600]"
                      : "text-[#1A1A1B]"
                      }`}
                    onClick={() => {
                      setSelectedType(opt.value);
                      setIsTypeOpen(false);
                      setPage(0);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* All Status dropdown */}
          <div ref={statusRef} className="relative">
            <div
              onClick={() => setIsStatusOpen(!isStatusOpen)}
              className="flex items-center gap-2 bg-[#F8F9FA] hover:bg-[#F0F1F2] transition-colors px-4 py-2.5 rounded-lg cursor-pointer text-[#1A1A1B] text-[14px] font-[500] border border-[#E6E6E7]"
            >
              <span>
                {statusOptions.find((opt) => opt.value === selectedStatus)
                  ?.label || "All Status"}
              </span>
              <KeyboardArrowDownIcon
                style={{ fontSize: "18px", color: "#1A1A1B" }}
              />
            </div>
            {isStatusOpen && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-white border border-[#E6E6E7] rounded-lg shadow-lg z-50 py-1">
                {statusOptions.map((opt) => (
                  <div
                    key={opt.value}
                    className={`px-4 py-2 hover:bg-[#F8F9FA] cursor-pointer text-[14px] ${selectedStatus === opt.value
                      ? "text-[#00BC67] font-[600]"
                      : "text-[#1A1A1B]"
                      }`}
                    onClick={() => {
                      setSelectedStatus(opt.value);
                      setIsStatusOpen(false);
                      setPage(0);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Duration dropdown */}
          <div ref={durationRef} className="relative">
            <div
              onClick={() => setIsDurationOpen(!isDurationOpen)}
              className="flex items-center gap-2 bg-[#F8F9FA] hover:bg-[#F0F1F2] transition-colors px-4 py-2.5 rounded-lg cursor-pointer text-[#1A1A1B] text-[14px] font-[500] border border-[#E6E6E7]"
            >
              <span>
                {durationOptions.find((opt) => opt.value === selectedDuration)
                  ?.label || "All Time"}
              </span>
              <KeyboardArrowDownIcon
                style={{ fontSize: "18px", color: "#1A1A1B" }}
              />
            </div>
            {isDurationOpen && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-white border border-[#E6E6E7] rounded-lg shadow-lg z-50 py-1">
                {durationOptions.map((opt) => (
                  <div
                    key={opt.value}
                    className={`px-4 py-2 hover:bg-[#F8F9FA] cursor-pointer text-[14px] ${selectedDuration === opt.value
                      ? "text-[#00BC67] font-[600]"
                      : "text-[#1A1A1B]"
                      }`}
                    onClick={() => {
                      setSelectedDuration(opt.value);
                      setIsDurationOpen(false);
                      setPage(0);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search Input */}
          {/* <input
            type="text"
            value={searchVal}
            onChange={(e) => {
              const val = e.target.value;
              setSearchVal(val);
              if (val === "") {
                setSearchQuery("");
                setPage(0);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearchQuery(searchVal);
                setPage(0);
              }
            }}
            placeholder="Reference/Transaction ID"
            className="bg-[#F8F9FA] border border-[#E6E6E7] text-[#1A1A1B] placeholder-[#808285] px-4 py-2.5 rounded-lg text-[14px] w-full sm:w-60 focus:outline-none"
          /> */}

          {/* Search Button */}
          {/* <button
            type="button"
            onClick={() => {
              setSearchQuery(searchVal);
              setPage(0);
            }}
            className="bg-[#00BC67] opacity-90 text-white px-6 py-2.5 rounded-lg font-[600] text-[14px] h-[38px] flex items-center justify-center"
          >
            Search
          </button> */}
        </div>
      </div>

      <Paper
        sx={{
          width: "100%",
          overflowX: "auto",
          boxShadow: "none",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: 440,
            minWidth: 650,
            borderBottom: "1px solid #D2D3D5",
          }}
        >
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Transaction type</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Transaction Id</StyledTableCell>
                <StyledTableCell align="left">Time/Date</StyledTableCell>
                <StyledTableCell align="left">Amount</StyledTableCell>
                <StyledTableCell align="left">Status</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array(5)
                  .fill(null)
                  .map((_, index) => (
                    <TableRow key={index}>
                      <StyledTableCell>
                        <Skeleton variant="text" width={140} height={20} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Skeleton variant="text" width={140} height={20} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Skeleton variant="text" width={140} height={20} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Skeleton variant="text" width={180} height={20} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Skeleton variant="text" width={100} height={20} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Skeleton variant="text" width={80} height={20} />
                      </StyledTableCell>
                    </TableRow>
                  ))
                : displayData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((tran, index) => {
                    const statusLabel = getStatusLabel(tran);
                    return (
                      <TableRow
                        key={tran.transactionId || index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <StyledTableCell component="th" scope="row">
                          <span
                            style={{
                              color: getTransactionTypeColor(tran),
                              fontWeight: "500",
                            }}
                          >
                            {getTransactionType(tran)}
                          </span>
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="row">
                          <span className="text-[#1A1A1B]">
                            {tran.description}
                          </span>
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="row">
                          <span className="text-[#1A1A1B]">
                            {tran.transactionId}
                          </span>
                        </StyledTableCell>
                        <StyledTableCell
                          align="left"
                          style={{
                            fontWeight: "400",
                            color: "#1A1A1B",
                          }}
                        >
                          {formatDate(tran.transactionDate || "")}
                        </StyledTableCell>
                        <StyledTableCell
                          align="left"
                          style={{ fontWeight: "400", color: "#1A1A1B" }}
                        >
                          {formatAmount(tran)}
                        </StyledTableCell>
                        <StyledTableCell align="">
                          <span
                            style={{
                              color: getStatusColor(statusLabel),
                              fontWeight: "400",
                            }}
                          >
                            {statusLabel}
                          </span>
                        </StyledTableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>

        {displayData.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={displayData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}

        {displayData.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center p-10">
            <img src={Note} alt="No transactions" className="mb-4 w-16 h-16" />
            <p className="text-[#808285] text-[16px] mt-2">
              You have not made any transaction
            </p>
          </div>
        )}
      </Paper>
    </div>
  );
}

export default Transactions;
